// CORE
// Este é um arquivo core do boilerplate e modificações neste arquivo serão bloqueadas via pré-hook de commit.
// Fazemos este bloqueio para manter a consistência do boilerplate e diminuir as chances de conflitos nos updates.
// Caso possível, procure uma alternativa ao uso deste arquivo.
// Se a alteração for realmente necessária ou for um update do boilerplate,
// realize o commit usando o comando 'git commit --no-verify'.

import { handleSubmitError } from '@/components/notification/defaults';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  QueryClient,
  UseQueryResult,
  UseMutationResult,
  UseQueryOptions,
  QueryKey,
} from '@tanstack/react-query';
import {
  create,
  keyResolver,
  windowedFiniteBatchScheduler,
} from '@yornaath/batshit';
import axios, { AxiosError } from 'axios';
import { useEffect } from 'react';
import { Cookies } from 'react-cookie';

const baseUrl = process.env.NEXT_PUBLIC_API_URL;

// Type definitions
type PathParamsArr = (string | number)[];
type QueryParams = Record<string, any>;

interface PaginatedResponse<T> {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results: T[];
}

interface QueryResult<T> {
  data: T[] | null;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  count?: number;
  next?: string | null;
  previous?: string | null;
  isPending?: boolean;
}

interface SideEffect {
  queryKey: QueryKey;
  data: any;
}

type QueryTransformFunction<T, R> = (element: T) => R;
type SideEffectFunction<T> = (element: T) => SideEffect[];

type SuccessCallback<T> = (
  queryClient: QueryClient,
  oldData: any,
  newData: T,
) => void;
type ErrorCallback = (
  error: AxiosError,
  queryClient: QueryClient,
  oldData: any,
  newData: any,
) => void;
type StartCallback = (
  queryClient: QueryClient,
  oldData: any,
  newData: any,
) => void;

interface ContextReq {
  req: {
    headers: {
      cookie?: string;
    };
  };
  res: any;
}

//queryFunction é OPCIONAL
export function useGetRequest<T = any>(
  paramsArr: PathParamsArr,
  queryFunction?: () => Promise<T>,
): UseQueryResult<T, AxiosError> {
  if (!queryFunction) {
    queryFunction = buildBatchedGetRequest<T & { id: string | number }>(
      paramsArr,
      {},
    );
  }

  return useQuery<T, AxiosError>({
    queryKey: paramsArr,
    queryFn: queryFunction,
  });
}

//queryParams é OBRIGATÓRIO e deve ser consistente com o queryParam das queries em array. Exemplo:
//  CommentArray => ["posts", 1, "comments"]
//  Comment => ["posts", 1, "comments", 1]

//queryTransformFunction é OPCIONAL. Ele modifica o valor visto pelo hook para os elementos. Não altera o que é armazenado no cache
// Assinatura esperada: (element) => tranformedElement

//sideEffectFunction é OPCIONAL. Ele é chamado com o valor retornado pela request, e deve retornar um array de sideEffects
// Assinatura esperada: (element) => [sideEffect1, sideEffect2, ...]
// O sideEffect é um objeto com os seguintes campos:
//  queryKey: Array com o queryKey da query que será atualizada
//  data: Novo valor da query

// IMPORTANTE: os side effects NÃO PODEM ALTERAR queries dentro da mesma queryKey fornecida para função, pois isso gera loop infinito
// CASO DE USO: A request do backend retorna dados de um objeto relacionado, e queremos atualizar o cache desse objeto relacionado. Ex:
// Comment: {id: 1, post: 1, content: "blablabla", author: {id: 1, name: "João"}}
// sideEffectFunction: (comment) => [{queryKey: ["users", comment.author.id], data: comment.author}]

export function useCustomGetRequest<T = any, R = T>(
  queryKey: PathParamsArr,
  queryFunction?: () => Promise<T>,
  queryTransformFunction?: QueryTransformFunction<T, R>,
  sideEffectFunction?: SideEffectFunction<T>,
): UseQueryResult<R, AxiosError> {
  if (!queryFunction) {
    queryFunction = buildBatchedGetRequest<T & { id: string | number }>(
      queryKey,
      {},
    );
  }
  const queryClient = useQueryClient();
  const result = useQuery<T, AxiosError, R>({
    queryKey,
    queryFn: queryFunction,
    select: (data: T): R => {
      if (queryTransformFunction) {
        return queryTransformFunction(data);
      }
      return data as unknown as R;
    },
  });

  // Use useEffect instead of onSuccess which was removed in React Query v5
  useEffect(() => {
    if (result.isSuccess && result.data && sideEffectFunction) {
      const sideEffects = sideEffectFunction(result.data as unknown as T);
      sideEffects.forEach((sideEffect) => {
        queryClient.setQueryData(sideEffect.queryKey, sideEffect.data);
      });
    }
  }, [result.data, result.isSuccess, sideEffectFunction, queryClient]);

  return result;
}

//queryFunction é OPCIONAL
export function useGetArrayRequest<T extends { id: string | number } = { id: string | number }>(
  paramsArr: PathParamsArr,
  queryFunction?: () => Promise<PaginatedResponse<T>>,
): QueryResult<T> {
  return useCustomGetArrayRequest<T, T>(
    paramsArr,
    {},
    queryFunction,
    null,
    null,
  );
}

//paramsArr é OBRIGATÓRIO e deve ser consistente com o paramsArr das queries individuais. Exemplo:
//  CommentArray => ["posts", 1, "comments"]
//  Comment => ["posts", 1, "comments", 1]

//queryParams é OPCIONAL. Caso fornecido (por exemplo, para paginação, filtrar, etc), ele será passado somente para a query do array, e não para as queries individuais
//queryFunction é OPCIONAL

//queryTransformFunction é OPCIONAL. Ele modifica o valor visto pelo hook para TODOS os elementos. Não altera o que é armazenado no cache
// Assinatura esperada: (arrayElement) => tranformedArrayElement

//sideEffectFunction é OPCIONAL. Ele é chamado com o valor de cada elemento do array, e deve retornar um array de sideEffects
// Assinatura esperada: (arrayElement) => [sideEffect1, sideEffect2, ...]
// O sideEffect é um objeto com os seguintes campos:
//  queryKey: Array com o queryKey da query que será atualizada
//  data: Novo valor da query

// IMPORTANTE: os side effects NÃO PODEM ALTERAR queries dentro da mesma queryKey fornecida para função, pois isso gera loop infinito
// CASO DE USO: A request do backend retorna dados de um objeto relacionado, e queremos atualizar o cache desse objeto relacionado. Ex:
// Comment: {id: 1, post: 1, content: "blablabla", author: {id: 1, name: "João"}}
// sideEffectFunction: (comment) => [{queryKey: ["users", comment.author.id], data: comment.author}]

export function useCustomGetArrayRequest<
  T extends { id: string | number },
  R = T,
>(
  paramsArr: PathParamsArr,
  queryParams: QueryParams,
  queryFunction?: () => Promise<PaginatedResponse<T>>,
  queryTransformFunction?: QueryTransformFunction<T, R> | null,
  sideEffectFunction?: SideEffectFunction<T> | null,
): QueryResult<R> {
  if (!queryFunction) {
    queryFunction = buildGetRequest<PaginatedResponse<T>>(
      paramsArr,
      queryParams,
    );
  }
  let count: number | undefined,
    next: string | null | undefined,
    previous: string | null | undefined;

  const queryClient = useQueryClient();
  const { data, isLoading, isError, isSuccess, ...props } = useQuery<
    PaginatedResponse<T>,
    AxiosError,
    R[]
  >({
    queryKey: [...paramsArr, queryParams],
    queryFn: queryFunction,
    staleTime: Infinity,
    select: (data: PaginatedResponse<T>): R[] => {
      count = data.count;
      next = data.next;
      previous = data.previous;
      if (queryTransformFunction) {
        return data.results.map((elem) => queryTransformFunction(elem));
      }
      return data.results as unknown as R[];
    },
  });

  useEffect(() => {
    if (isSuccess && data && sideEffectFunction) {
      data.forEach((arrayObj) => {
        const sideEffects = sideEffectFunction(arrayObj as unknown as T);
        sideEffects.forEach((sideEffect) => {
          queryClient.setQueryData(sideEffect.queryKey, sideEffect.data);
        });
      });
    }
  }, [data, isSuccess, sideEffectFunction, queryClient]);

  let queriesArr: UseQueryOptions<T, AxiosError>[] = [];
  if (isSuccess && data) {
    queriesArr = data.map((elem) => {
      const typedElem = elem as unknown as T;
      return {
        queryKey: [...paramsArr, typedElem.id],
        queryFn: buildBatchedGetRequest<T>([...paramsArr, typedElem.id], {}),
        initialData: typedElem,
        enabled: isSuccess,
      };
    });
  }

  const queries = useQueries({
    queries: queriesArr,
    combine: (results) => {
      return {
        data: results.map((result) => result.data) as R[],
        isPending: results.some((result) => result.isPending),
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
        isSuccess: results.every((result) => result.isSuccess),
      };
    },
  });

  useEffect(() => {
    if (queries.data && sideEffectFunction) {
      queries.data.forEach((data) => {
        if (data) {
          const sideEffects = sideEffectFunction(data as unknown as T);
          sideEffects.forEach((sideEffect) => {
            queryClient.setQueryData(sideEffect.queryKey, sideEffect.data);
          });
        }
      });
    }
  }, [queries.data, sideEffectFunction, queryClient]);

  if (!isSuccess) {
    return { data: null, isLoading, isError, isSuccess };
  }

  return {
    ...queries,
    count: count,
    next: next,
    previous: previous,
    ...props,
  };
}

//pathParamsArr é OBRIGATÓRIO e deve ser consistente com o pathParamsArr do GET
//Ex: GET =>  Comment => ["posts", 1, "comments", 1]
//    POST =>  Comment => ["posts", 1, "comments"]

//successCallback, errorCallback, startCallback são OPCIONAIS

// Se não forem passadas, o comportamento padrão será executado
// Se forem passadas, o comportamento padrão NÃO SERÁ EXECUTADO

// A assinatura das funcoes e o comportamento padrão é:

// 1. successCallback:
// (queryClient, oldData, newData) => {}
//  Comportamento padrão: Atualiza o react-query do elemento com o novo dado baseado no id retornado

// 2. errorCallback:
// (error, queryClient, oldData, newData) => {}
//  Comportamento padrão: Notistack de erro

// 3. startCallback:
// (queryClient, oldData, newData) => {}
// Comportamento padrão: Nada
// Observação: Se o startCallback é fornecido, o queryClient.cancelQueries é chamado automaticamente

export function usePostRequest<
  T extends { id?: string | number } = any,
  R = T,
>(
  pathParamsArr: PathParamsArr,
  successCallback?: SuccessCallback<R>,
  errorCallback?: ErrorCallback,
  startCallback?: StartCallback,
  queryFunction?: (requestData: T) => Promise<R>,
): UseMutationResult<R, AxiosError, T> {
  const queryClient = useQueryClient();

  return useMutation<R, AxiosError, T, { oldData: any }>({
    mutationFn: (requestData: T) => {
      if (!queryFunction) {
        return buildPostRequest<T, R>(pathParamsArr, requestData);
      }
      return queryFunction(requestData);
    },
    onMutate: async (newData: T) => {
      await queryClient.cancelQueries({ queryKey: pathParamsArr });
      const oldData = queryClient.getQueryData(pathParamsArr);
      if (startCallback) {
        startCallback(queryClient, oldData, newData);
      }
      return { oldData };
    },
    onSuccess: (newData: R, _variables: T, context) => {
      if (successCallback) {
        successCallback(queryClient, context.oldData, newData);
      } else {
        const dataWithId = newData as any;
        if (dataWithId?.id) {
          queryClient.setQueryData([...pathParamsArr, dataWithId.id], newData);
        }
      }
    },
    onError: (error: AxiosError, variables: T, context) => {
      if (errorCallback) {
        errorCallback(error, queryClient, context?.oldData, variables);
      } else {
        handleSubmitError(error as unknown as Parameters<typeof handleSubmitError>[0], undefined);
      }
    },
  });
}

//successCallback, errorCallback, startCallback são OPCIONAIS

// Se não forem passadas, o comportamento padrão será executado
// Se forem passadas, o comportamento padrão NÃO SERÁ EXECUTADO

// A assinatura das funcoes e o comportamento padrão é:

// 1. successCallback:
// (queryClient, oldData, newData) => {}
//  Comportamento padrão: Atualiza o react-query do elemento com o novo dado baseado no pathParamArr fornecido
// 2. errorCallback:
// (error, queryClient, oldData, newData) => {}
//  Comportamento padrão: Notistack de erro

// 3. startCallback:
// (queryClient, oldData, newData) => {}
// Comportamento padrão: Nada
// Observação: Se o startCallback é fornecido, o queryClient.cancelQueries é chamado automaticamente

export function usePutRequest<T extends { id: string | number }, R = T>(
  pathParamsArr: PathParamsArr,
  successCallback?: SuccessCallback<R>,
  errorCallback?: ErrorCallback,
  startCallback?: StartCallback,
  queryFunction?: (requestData: T) => Promise<R>,
): UseMutationResult<R, AxiosError, T> {
  const queryClient = useQueryClient();

  return useMutation<R, AxiosError, T, { oldData: any }>({
    mutationFn: (requestData: T) => {
      if (!queryFunction) {
        return buildPutRequest<T, R>(
          [...pathParamsArr, requestData.id],
          requestData,
        );
      }
      return queryFunction(requestData);
    },
    onMutate: async (newData: T) => {
      const queryKey = [...pathParamsArr, newData.id];
      const oldData = queryClient.getQueryData(queryKey);
      if (startCallback) {
        await queryClient.cancelQueries({ queryKey: queryKey });
        startCallback(queryClient, oldData, newData);
      }
      return { oldData };
    },
    onSuccess: (newData: R, _variables: T, context) => {
      const queryKey = [...pathParamsArr, (_variables as T).id];
      if (successCallback)
        successCallback(queryClient, context.oldData, newData);
      else {
        queryClient.setQueryData(queryKey, newData);
      }
    },
    onError: (error: AxiosError, variables: T, context) => {
      if (errorCallback)
        errorCallback(error, queryClient, context?.oldData, variables);
      else {
        handleSubmitError(error as unknown as Parameters<typeof handleSubmitError>[0], undefined);
      }
    },
  });
}

//successCallback, errorCallback, startCallback são OPCIONAIS

// Se não forem passadas, o comportamento padrão será executado
// Se forem passadas, o comportamento padrão NÃO SERÁ EXECUTADO

// A assinatura das funcoes e o comportamento padrão é:

// 1. successCallback:
// (queryClient, oldData, newData) => {}
//  Comportamento padrão: Atualiza o react-query do elemento com o novo dado baseado no pathParamArr fornecido
// 2. errorCallback:
// (error, queryClient, oldData, newData) => {}
//  Comportamento padrão: Notistack de erro

// 3. startCallback:
// (queryClient, oldData, newData) => {}
// Comportamento padrão: Nada
// Observação: Se o startCallback é fornecido, o queryClient.cancelQueries é chamado automaticamente

export function usePatchRequest<T extends { id: string | number }, R = T>(
  pathParamsArr: PathParamsArr,
  successCallback?: SuccessCallback<R>,
  errorCallback?: ErrorCallback,
  startCallback?: StartCallback,
  queryFunction?: (requestData: T) => Promise<R>,
): UseMutationResult<R, AxiosError, T> {
  const queryClient = useQueryClient();

  return useMutation<R, AxiosError, T, { oldData: any }>({
    mutationFn: (requestData: T) => {
      if (!queryFunction) {
        return buildPatchRequest<T, R>(
          [...pathParamsArr, requestData.id],
          requestData,
        );
      }
      return queryFunction(requestData);
    },
    onMutate: async (newData: T) => {
      const queryKey = [...pathParamsArr, newData.id];
      const oldData = queryClient.getQueryData(queryKey);
      if (startCallback) {
        await queryClient.cancelQueries({ queryKey: queryKey });
        startCallback(queryClient, oldData, newData);
      }
      return { oldData };
    },
    onSuccess: (newData: R, _variables: T, context) => {
      const queryKey = [...pathParamsArr, (_variables as T).id];
      if (successCallback)
        successCallback(queryClient, context.oldData, newData);
      else {
        queryClient.setQueryData(queryKey, newData);
      }
    },
    onError: (error: AxiosError, variables: T, context) => {
      if (errorCallback)
        errorCallback(error, queryClient, context?.oldData, variables);
      else {
        handleSubmitError(error as unknown as Parameters<typeof handleSubmitError>[0], undefined);
      }
    },
  });
}

//successCallback, errorCallback, startCallback são OPCIONAIS

// Se não forem passadas, o comportamento padrão será executado
// Se forem passadas, o comportamento padrão NÃO SERÁ EXECUTADO

// A assinatura das funcoes e o comportamento padrão é:

// 1. successCallback:
// (queryClient, oldData, newData) => {}
//  Comportamento padrão: Atualiza o react-query do elemento com o novo dado baseado no pathParamArr fornecido
// 2. errorCallback:
// (error, queryClient, oldData, newData) => {}
//  Comportamento padrão: Notistack de erro

// 3. startCallback:
// (queryClient, oldData, newData) => {}
// Comportamento padrão: Nada
// Observação: Se o startCallback é fornecido, o queryClient.cancelQueries é chamado automaticamente

export function useDeleteRequest<T extends { id: string | number }>(
  pathParamsArr: PathParamsArr,
  successCallback?: SuccessCallback<T>,
  errorCallback?: ErrorCallback,
  startCallback?: StartCallback,
  queryFunction?: (requestData: T) => Promise<T>,
): UseMutationResult<T, AxiosError, T> {
  const queryClient = useQueryClient();

  return useMutation<T, AxiosError, T, { oldData: any }>({
    mutationFn: (requestData: T) => {
      if (!queryFunction) {
        return buildDeleteRequest<T>(
          [...pathParamsArr, requestData.id],
          requestData,
        );
      }
      return queryFunction(requestData);
    },
    onMutate: async (newData: T) => {
      const queryKey = [...pathParamsArr, newData.id];
      const oldData = queryClient.getQueryData(queryKey);
      if (startCallback) {
        await queryClient.cancelQueries({ queryKey: queryKey });
        startCallback(queryClient, oldData, newData);
      }
      return { oldData };
    },
    onSuccess: (newData: T, _variables: T, context) => {
      const queryKey = [...pathParamsArr, newData.id];
      if (successCallback)
        successCallback(queryClient, context.oldData, newData);
      else {
        queryClient.removeQueries({ queryKey: queryKey });
      }
    },
    onError: (error: AxiosError, variables: T, context) => {
      if (errorCallback)
        errorCallback(error, queryClient, context?.oldData, variables);
      else {
        handleSubmitError(error as unknown as Parameters<typeof handleSubmitError>[0], undefined);
      }
    },
  });
}

const batcherStorage: Record<string, any> = {};

export function buildBatchedGetRequest<T extends { id: string | number }>(
  pathParamsArr: PathParamsArr,
  queryParams: QueryParams,
): () => Promise<T> {
  const batcherStorageKey = pathParamsArr.slice(0, -1).join('/');
  let batcher = batcherStorage[batcherStorageKey];
  if (batcherStorage[batcherStorageKey] == undefined) {
    batcherStorage[batcherStorageKey] = create({
      fetcher: async (ids: (string | number)[]) => {
        const request = buildGetRequest<PaginatedResponse<T>>(
          pathParamsArr.slice(0, -1),
          {
            ids: ids,
            ...queryParams,
          },
          null,
        );

        const result = await request();
        return result.results;
      },
      resolver: keyResolver('id'),
      scheduler: windowedFiniteBatchScheduler({
        windowMs: 40,
        maxBatchSize: 200,
      }),
    });
    batcher = batcherStorage[batcherStorageKey];
  }

  const id = pathParamsArr.slice(-1)[0];
  return async () => {
    return batcher.fetch(String(id));
  };
}

//Função que pode ser usada ao invés de buildBatchedGetRequest e buildGetRequest para casos que se quer fazer batch de requisições do tipo:
// GET /posts?author=1
// GET /posts?author=2
// GET /posts?author=3
// Para /posts?author=1,2,3
// queryParamsConstant é o nome do parâmetro dentro do "queryParams" que é constante para todas as requisições. No exemplo acima, seria "author"
// Todos os outros parametros do queryParams deverão ser constantes para utilizar essa função

// resultKeyResolver é o nome do campo que será utilizado para filtrar os resultados. Por default, é o mesmo que o queryParamsConstant.
// Mas, em alguns casos que a resposta da requisição foge do padrao, pode ser necessário utilizar um campo diferente para filtrar os resultados. Exemplo:
// GET /posts?author=1,2,3
// {
//   results: [
//     {id: 1, author_id: 1},
//     {id: 2, author_id: 1},
//     {id: 3, author_id: 3},
//   ]
// }
// Nesse caso, o queryParamsConstant seria "author" e o resultKeyResolver seria "author_id"

export function buildBatchedGetRequestForArray<T>(
  pathParamsArr: PathParamsArr,
  queryParams: QueryParams,
  queryParamsConstant: string,
  resultKeyResolver: string | false = false,
): () => Promise<T[]> {
  if (!resultKeyResolver) resultKeyResolver = queryParamsConstant;

  const queryParamsWithoutConstant = { ...queryParams };
  delete queryParamsWithoutConstant[queryParamsConstant];

  const batcherStorageKey =
    pathParamsArr.join('/') +
    JSON.stringify(
      queryParamsWithoutConstant,
      Object.keys(queryParamsWithoutConstant).sort(),
    );
  let batcher = batcherStorage[batcherStorageKey];
  if (batcherStorage[batcherStorageKey] == undefined) {
    batcherStorage[batcherStorageKey] = create({
      fetcher: async (queryParamsArr: QueryParams[]) => {
        const queryParams = { ...queryParamsWithoutConstant };
        queryParams[queryParamsConstant] = [];

        queryParamsArr.forEach((params) => {
          queryParams[queryParamsConstant].push(params[queryParamsConstant]);
        });

        const request = buildGetRequest<PaginatedResponse<T>>(
          pathParamsArr,
          queryParams,
          null,
        );

        const result = await request();
        return result.results;
      },
      resolver: (results: T[], query: QueryParams) => {
        return results.filter((result: any) => {
          if (Array.isArray(result[resultKeyResolver as string]))
            return result[resultKeyResolver as string].includes(
              query[queryParamsConstant],
            );
          if (Array.isArray(query[queryParamsConstant]))
            return query[queryParamsConstant].includes(
              result[resultKeyResolver as string],
            );

          return (
            result[resultKeyResolver as string] == query[queryParamsConstant]
          );
        });
      },
      scheduler: windowedFiniteBatchScheduler({
        windowMs: 40,
        maxBatchSize: 200,
      }),
    });
    batcher = batcherStorage[batcherStorageKey];
  }

  return async () => {
    return batcher.fetch(queryParams);
  };
}

export function buildGetRequest<T>(
  pathParamsArr: PathParamsArr,
  queryParamsArr: QueryParams,
  contextReq?: ContextReq | null,
): () => Promise<T> {
  const params: any = {
    method: 'GET',
    withCredentials: true,
    url: '',
  };

  if (contextReq) {
    params.headers = {
      Cookie: getAuthCookie(contextReq, params),
    };
  }

  params.url = buildURL(baseUrl!, pathParamsArr, queryParamsArr);

  return async function () {
    return axios(params).then((res) => {
      return res.data;
    });
  };
}

export function buildListRequest<T>(
  pathParamsArr: PathParamsArr,
  queryParamsArr: QueryParams,
  contextReq?: ContextReq | null,
): () => Promise<T> {
  const params: any = {
    method: 'GET',
    withCredentials: true,
    url: '',
  };

  if (contextReq) {
    params.headers = {
      Cookie: getAuthCookie(contextReq, params),
    };
  }

  params.url = buildURL(baseUrl!, pathParamsArr, queryParamsArr);

  return async function () {
    return axios(params).then((res) => {
      return res.data;
    });
  };
}

export async function buildPostRequest<T, R = T>(
  pathParamsArr: PathParamsArr,
  bodyDict: T,
): Promise<R> {
  const params = {
    method: 'POST',
    withCredentials: true,
    url: '',
    data: bodyDict,
  };

  params.url = buildURL(baseUrl!, pathParamsArr, {});

  return axios(params).then((res) => res.data);
}

export async function buildPutRequest<T, R = T>(
  pathParamsArr: PathParamsArr,
  bodyDict: T,
): Promise<R> {
  const params = {
    method: 'PUT',
    withCredentials: true,
    url: '',
    data: bodyDict,
  };

  params.url = buildURL(baseUrl!, pathParamsArr, {});

  return axios(params).then((res) => res.data);
}

export async function buildPatchRequest<T, R = T>(
  pathParamsArr: PathParamsArr,
  bodyDict: T,
): Promise<R> {
  const params = {
    method: 'PATCH',
    withCredentials: true,
    url: '',
    data: bodyDict,
  };

  params.url = buildURL(baseUrl!, pathParamsArr, {});

  return axios(params).then((res) => res.data);
}

export async function buildDeleteRequest<T>(
  pathParamsArr: PathParamsArr,
  bodyDict: T,
): Promise<T> {
  const params = {
    method: 'DELETE',
    withCredentials: true,
    url: '',
    data: bodyDict,
  };

  params.url = buildURL(baseUrl!, pathParamsArr, {});

  return axios(params).then((res) => res.data);
}

function getAuthCookie(contextReq: ContextReq, _params: any): string {
  const cookie = new Cookies(contextReq.req as any, contextReq.res);
  const sessionCookie = cookie.get('sessionid');

  return 'sessionid=' + sessionCookie + ';';
}

function buildURL(
  baseUrl: string,
  pathParamsArr: PathParamsArr,
  queryParamsArr: QueryParams,
): string {
  let url: string = pathParamsArr.reduce<string>((url, param) => {
    if (['string', 'number'].includes(typeof param)) {
      url += param + '/';
    }
    return url;
  }, baseUrl);

  Object.entries(queryParamsArr).forEach(([key, value], index) => {
    if (index == 0) url += '?';
    if (index != 0) url += '&';

    if (Array.isArray(value)) {
      if (value.length == 1) {
        url += key + '=' + value[0];
        return;
      } else if (value.length != 0) {
        url += key + '=' + value.join(',');
      }
    } else {
      url += key + '=' + value;
    }
  });

  return url;
}
